import { find, isEmpty, map } from 'lodash';
import moment from 'moment';
import { IShopifyPromoCodeFormOptions, IShopifyPromoCodeFormValues, PrefixTypeStrategy, ProductCollections, PROMO_OFFER_TYPES, PurchaseType, RecurringCycleLimit } from '../components';
import { BOOST_TYPE, BoostInput, OFFER_PAYOUT_TYPE_PROMO, OFFER_PROMO_PREFIX_TYPE, OFFER_SOURCE, UpdatePromoInput } from '../types/globalTypes';
import { GetOfferById_offer_boosts, GetOfferById_offer_boosts_discountOffer_promos } from '../queries/types/GetOfferById';

export function transformDiscountBoostInputFromApi(apiDiscountBoost: GetOfferById_offer_boosts_discountOffer_promos): UpdatePromoInput {
  return {
    codeSuffix: apiDiscountBoost.codeSuffix,
    codePrefix: apiDiscountBoost.codePrefix,
    flatPayout: apiDiscountBoost.flatPayout,
    percentPayout: apiDiscountBoost.percentPayout,
    payoutType: apiDiscountBoost.payoutType,
    payouts: [],
    name: apiDiscountBoost.name,
    source: apiDiscountBoost.source,
    specialLimitOnePerSale: apiDiscountBoost.specialLimitOnePerSale,
    specialLimitUsageCapAmount: apiDiscountBoost.specialLimitUsageCapAmount,
    specialLimitUsageCapEnabled: apiDiscountBoost.specialLimitUsageCapEnabled,
    status: apiDiscountBoost.status,
    multiShopifyEnabled: apiDiscountBoost.multiShopifyEnabled,
    isSecureCodes: apiDiscountBoost.isSecureCodes,
    connectedClientMetadata: apiDiscountBoost.connectedClientMetadata,
    isSubscription: apiDiscountBoost.isSubscription,
    isOneTime: apiDiscountBoost.isOneTime,
    recurringCycleLimit: apiDiscountBoost.recurringCycleLimit,
    priceRuleAmount: apiDiscountBoost.priceRuleAmount,
    priceRuleType: apiDiscountBoost.priceRuleType,
    usageLimitAmount: apiDiscountBoost.usageLimitAmount,
    usageLimitRule: apiDiscountBoost.usageLimitRule,
    productCollections: apiDiscountBoost.providerMetadata?.productCollections,
    startDate: apiDiscountBoost.startDate ? moment(apiDiscountBoost.startDate) : null,
    endDate: apiDiscountBoost.endDate ? moment(apiDiscountBoost.endDate) : null,
    clientsForSync: [],
    discountCombination: {
      productDiscounts: false,
      orderDiscounts: false,
      shippingDiscounts: false,
    },
  };
}
export function transformBoostFromApi(apiBoost: GetOfferById_offer_boosts): BoostInput {
  return {
    activeEndDate: apiBoost.activeEndDate ? new Date(apiBoost.activeEndDate) : null,
    activeStartDate: apiBoost.activeStartDate ? new Date(apiBoost.activeStartDate) : null,
    boostType: apiBoost.boostType,
    label: apiBoost.label,
    maxLimit: apiBoost.maxLimit,
    triggerType: apiBoost.triggerType,
    triggerValue: apiBoost.triggerValue,
    id: apiBoost.id,
    hideInCreatorPortal: apiBoost.hideInCreatorPortal ?? undefined,
    requireApproval: apiBoost.requireApproval ?? undefined,
    discountBoostInput:
      apiBoost.boostType === BOOST_TYPE.DISCOUNT_CODE && apiBoost.discountOffer?.promos?.length
        ? transformDiscountBoostInputFromApi(apiBoost.discountOffer.promos[0])
        : null,
  };
}
export const processPromoCodeInput = (values: Partial<IShopifyPromoCodeFormValues>, allSubscriptionEligible: boolean) => {
    let flatPayout = null;
    let percentPayout = null;
    switch (values.payoutType) {
      case OFFER_PAYOUT_TYPE_PROMO.CONVERSION:
        flatPayout = Number(values.flatPayout);
        break;
      case OFFER_PAYOUT_TYPE_PROMO.SALE:
        percentPayout = Number(values.percentPayout);
        break;
      case OFFER_PAYOUT_TYPE_PROMO.CONVERSION_AND_SALE:
        flatPayout = Number(values.flatPayout);
        percentPayout = Number(values.percentPayout);
        break;
    }
    let prefixType = OFFER_PROMO_PREFIX_TYPE.FULL_NAME;
    let codeSuffix = '';
    switch (values.prefixType) {
      case PrefixTypeStrategy.FULL_NAME_ONLY:
        break;
      case PrefixTypeStrategy.FULL_NAME_PLUS_SUFFIX:
        codeSuffix = values.codeSuffix;
        break;
      case PrefixTypeStrategy.INSTAGRAM_PLUS_SUFFIX:
        prefixType = OFFER_PROMO_PREFIX_TYPE.IG_USERNAME;
        codeSuffix = values.codeSuffix;
        break;
      case PrefixTypeStrategy.INSTAGRAM_ONLY:
        prefixType = OFFER_PROMO_PREFIX_TYPE.IG_USERNAME;
        break;
      case PrefixTypeStrategy.FIRST_INITIAL_LAST_NAME_PLUS_SUFFIX:
        prefixType = OFFER_PROMO_PREFIX_TYPE.FIRST_INITIAL_LAST_NAME;
        codeSuffix = values.codeSuffix;
        break;
      case PrefixTypeStrategy.FIRST_INITIAL_LAST_NAME_ONLY:
        prefixType = OFFER_PROMO_PREFIX_TYPE.FIRST_INITIAL_LAST_NAME;
        break;
      case PrefixTypeStrategy.FIRST_NAME_LAST_INITIAL_PLUS_SUFFIX:
        prefixType = OFFER_PROMO_PREFIX_TYPE.FIRST_NAME_LAST_INITIAL;
        codeSuffix = values.codeSuffix;
        break;
      case PrefixTypeStrategy.FIRST_NAME_LAST_INITIAL_ONLY:
        prefixType = OFFER_PROMO_PREFIX_TYPE.FIRST_NAME_LAST_INITIAL;
        break;
    }
    let recurringCycleLimitAmount;
    switch (values.offerCodePurchaseRestrictionsRule) {
      case RecurringCycleLimit.LIMIT_DISCOUNT_TO_THE_FIRST_PAYMENT:
        recurringCycleLimitAmount = 1;
        break;
      case RecurringCycleLimit.LIMIT_DISCOUNT_TO_MULTIPLE_RECURRING_PAYMENTS:
        recurringCycleLimitAmount = values.recurringCycleLimitAmount.toString();
        break;
      case RecurringCycleLimit.DISCOUNT_APPLIES_TO_ALL_RECURRING_PAYMENTS:
        recurringCycleLimitAmount = 0;
        break;
      default:
        recurringCycleLimitAmount = 0;
        break;
    }

    let isOneTime = false;
    let isSubscription = false;
    if (allSubscriptionEligible) {
      switch (values.purchaseType) {
        case PurchaseType.ONE_TIME:
          isOneTime = true;
          isSubscription = false;
          break;
        case PurchaseType.SUBSCRIPTION:
          isSubscription = true;
          isOneTime = false;
          break;
        case PurchaseType.BOTH:
          isOneTime = true;
          isSubscription = true;
          break;
        default:
          break;
      }
    }
    return {
        flatPayout,
        percentPayout,
        prefixType,
        codeSuffix,
        recurringCycleLimitAmount,
        isOneTime,
        isSubscription,
      };
};

export const transformDiscountBoostInputToPromoCodeValuesForBoost = (
  discountInput: UpdatePromoInput,
): Partial<IShopifyPromoCodeFormValues> => {
  console.log('Logging input for boost discount EDIT', discountInput);
  let purchaseType: PurchaseType | undefined;
  if (discountInput.isOneTime && discountInput.isSubscription) purchaseType = PurchaseType.BOTH;
  else if (discountInput.isOneTime) purchaseType = PurchaseType.ONE_TIME;
  else if (discountInput.isSubscription) purchaseType = PurchaseType.SUBSCRIPTION;

  let recurringCycleLimitAmount;
  switch (discountInput.recurringCycleLimit) {
    case 1:
      recurringCycleLimitAmount = 1;
      break;
    case 0:
      recurringCycleLimitAmount = 0;
      break;
    default:
      recurringCycleLimitAmount = discountInput.recurringCycleLimit.toString();
      break;
  }

  return {
    codeSuffix: discountInput.codeSuffix,
    description: '',
    flatPayout: null,
    groupName: discountInput.name,
    imageUrl: null,
    icon: null,
    name: discountInput.name,
    payoutType: null,
    payoutOptions: null,
    percentPayout: null,
    prefixType: PrefixTypeStrategy.FIRST_INITIAL_LAST_NAME_ONLY,
    priceRuleAmount: discountInput.priceRuleAmount != null
      ? discountInput.priceRuleAmount.toString()
      : '',
    priceRuleType: discountInput.priceRuleType,
    source: OFFER_SOURCE.SHOPIFY,
    specialLimitNewCustomersOnly: discountInput.specialLimitNewCustomersOnly,
    specialLimitOnePerSale: discountInput.specialLimitOnePerSale,
    specialLimitUsageCapAmount: discountInput.specialLimitUsageCapAmount?.toString(),
    specialLimitUsageCapEnabled: discountInput.specialLimitUsageCapEnabled,
    usageLimitAmount: discountInput.usageLimitAmount?.toString(),
    usageLimitRule: discountInput.usageLimitRule,
    productCollections: map(discountInput.productCollections, (collection) => collection.id),
    status: discountInput.status,
    activeTime: null,
    activeDate: discountInput.startDate,
    endDate: discountInput.endDate,
    endTime: null,
    recurringCycleLimitAmount,
    offerCodePurchaseRestrictionsRule: discountInput.recurringCycleLimit === 1 ? RecurringCycleLimit.LIMIT_DISCOUNT_TO_THE_FIRST_PAYMENT : discountInput.recurringCycleLimit === 0 ? RecurringCycleLimit.DISCOUNT_APPLIES_TO_ALL_RECURRING_PAYMENTS : RecurringCycleLimit.LIMIT_DISCOUNT_TO_MULTIPLE_RECURRING_PAYMENTS,
    isEndDateEnable: false,
    isNewFlow: true,
    purchaseType,
    isMultipleShopifySyncEnabled: false,
    isSameDiscountMultipleShopify: true,
    multiShopifyEnabled: false,
    clientsForSync: [],
    connectedAdvertiserForSync: [],
    allClientForSync: [],
    isUngrouped: false,
    lockEditing: [],
    productDiscounts: false,
    shippingDiscounts: false,
    orderDiscounts: false,
    isPromoLink: false,
    isLandingPageEnabled: false,
    landingPageUrl: null,
    shopifyRedirectUrl: null,
    isSecureCodes: false,
    linkedShopifyOfferId: '',
    isReadOnly: false,
    offerType: PROMO_OFFER_TYPES.AMOUNT_OFF,
  };
};

export const processProductCollections = (productCollectionIds: number[], productCollections: ProductCollections[]) => {
  console.log({ productCollectionIds, productCollections });
  const productCollectionsForInput = map(productCollectionIds, (collectionId) => {
    const collections = find(productCollections, (collection) => collection.id === collectionId);
    return {
      title: collections.title,
      id: collections.id,
    };
  });
  return productCollectionsForInput;
};

export const transformDiscountToUpdatePromoInput = (
  values: Partial<IShopifyPromoCodeFormValues>,
  formOptionsData: IShopifyPromoCodeFormOptions,
  isSubscriptionEnable: boolean,
): UpdatePromoInput => {
  const {
    prefixType,
    codeSuffix,
    isOneTime,
    isSubscription,
    recurringCycleLimitAmount: recurringCycleLimit,
  } = processPromoCodeInput(values, isSubscriptionEnable);
  return {
    codeSuffix,
    flatPayout: 0,
    payouts: [],
    name: values.groupName,
    payoutType: values.payoutType,
    percentPayout: 0,
    prefixType,
    priceRuleAmount: Number(values.priceRuleAmount),
    priceRuleType: values.priceRuleType,
    source: OFFER_SOURCE.SHOPIFY,
    specialLimitProductCollections: !isEmpty(values?.productCollections) || values?.productCollections == null,
    productCollections: processProductCollections(
      values?.productCollections,
      formOptionsData?.productCollectionOptions,
    ),
    specialLimitNewCustomersOnly: !!values.specialLimitNewCustomersOnly,
    specialLimitOnePerSale: !!values.specialLimitOnePerSale,
    specialLimitUsageCapAmount: Number(values.specialLimitUsageCapAmount) || null,
    specialLimitUsageCapEnabled: !!values.specialLimitUsageCapEnabled,
    usageLimitAmount: Number(values.usageLimitAmount) || null,
    usageLimitRule: values.usageLimitRule,
    status: values.status,
    isUngrouped: values.isUngrouped,
    recurringCycleLimit,
    isSubscription,
    isOneTime,
    codePrefix: values.isPrefixSelected || values.codePrefix ? values.codePrefix : '',
    startDate: values.activeDate,
    endDate: values.isEndDateEnable ? values.endDate : null,
    multiShopifyEnabled: false,
    clientsForSync: [],
    discountCombination: {
      productDiscounts: false,
      orderDiscounts: false,
      shippingDiscounts: false,
    },
    isSecureCodes: false,
  };
};

export function getEmptyBoostInput(boostType: BOOST_TYPE): BoostInput {
  return {
    label: '',
    boostType, // make this field nullable
    triggerType: '',
    triggerValue: 0,
    activeEndDate: null,
    activeStartDate: null,
    maxLimit: null,
    hideInCreatorPortal: false,
    requireApproval: false,
  };
}
